// The product form's state and its one conversion to `ProductIn` (A3): empty text is null, blank
// list rows are dropped, and the five image slots become the images that have an asset.
import type { Product, ProductImageTag } from "../../api/generated/model";
import { ProductIn, productInImagesMax } from "../../api/generated/zod";
import { colorDrafts, colorsIn, orNull, type ColorDraft } from "./drafts";
import { defaultTag } from "./TagSelect";

export interface ImageSlot {
  assetId: string;
  tag: ProductImageTag;
  /** The picture the slot shows, never sent: the image's thumbnail, else its file, as
   *  `pictureOf` gives a picked one (spec ui-perf/product-images). */
  url: string | null;
}

export interface ProductDraft {
  name: string;
  description: string;
  tagline: string;
  price: string;
  offer: string;
  /** Live's "Use product colors": off, the product follows the brand's and sends none. */
  useProductColors: boolean;
  colors: ColorDraft[];
  keyBenefits: string[];
  useCases: string[];
  images: ImageSlot[];
  /** The slot that holds the main image: the first, unless it is empty. */
  mainSlot: number;
}

/** An empty slot keeps the tag live gives its position. */
export function emptySlot(slot: number): ImageSlot {
  return { assetId: "", tag: defaultTag(slot), url: null };
}

export function productDraft(product?: Product): ProductDraft {
  // The main image goes first, as live's first slot is the product shot.
  const saved = [...(product?.images ?? [])].sort(
    (a, b) => Number(b.is_main === true) - Number(a.is_main === true),
  );
  // A slot draws a few hundred pixels wide: the 512 px thumbnail, not the file (a product page
  // read five originals, 7.2 MB, on dev; spec ui-perf/product-images).
  const images = saved.map((image) => ({
    assetId: image.asset_id,
    tag: image.tag,
    url: image.url == null ? null : (image.thumb_url ?? image.url),
  }));
  const empty = Array.from({ length: productInImagesMax }, (_, slot) => emptySlot(slot));
  return {
    name: product?.name ?? "",
    description: product?.description ?? "",
    tagline: product?.tagline ?? "",
    price: product?.price ?? "",
    offer: product?.offer ?? "",
    useProductColors: (product?.colors.length ?? 0) > 0,
    colors: colorDrafts(product?.colors ?? []),
    keyBenefits: product?.key_benefits ?? [],
    useCases: product?.use_cases ?? [],
    images: [...images, ...empty.slice(images.length)].slice(0, productInImagesMax),
    mainSlot: 0,
  };
}

/** Whether two drafts hold the same product: the pictures' signed URLs aside, which a new read
 *  signs anew (the leave prompt's "unsaved change", spec A3d). */
export function sameDraft(a: ProductDraft, b: ProductDraft): boolean {
  const kept = (draft: ProductDraft) =>
    JSON.stringify({ ...draft, images: draft.images.map(({ assetId, tag }) => ({ assetId, tag })) });
  return kept(a) === kept(b);
}

function rows(items: string[]): string[] {
  return items.map((item) => item.trim()).filter((item) => item !== "");
}

/** The slots that hold an asset, with their slot numbers, so errors find their slot. */
export function filledSlots(draft: ProductDraft) {
  return draft.images
    .map((slot, index) => ({ ...slot, assetId: slot.assetId.trim(), index }))
    .filter((slot) => slot.assetId !== "");
}

/**
 * A slot whose asset an earlier slot already holds. The API refuses it too, but its answer names
 * no field (A3 decision 7), so the form says which slot.
 */
export function repeatedSlot(draft: ProductDraft): number | undefined {
  const seen = new Set<string>();
  for (const slot of filledSlots(draft)) {
    if (seen.has(slot.assetId.toLowerCase())) return slot.index;
    seen.add(slot.assetId.toLowerCase());
  }
  return undefined;
}

/** The main image's slot: the chosen one while it holds an asset, else the first that does. */
export function mainSlotOf(draft: ProductDraft): number | undefined {
  const filled = filledSlots(draft);
  return filled.some((slot) => slot.index === draft.mainSlot) ? draft.mainSlot : filled[0]?.index;
}

export function productIn(draft: ProductDraft) {
  const filled = filledSlots(draft);
  const main = mainSlotOf(draft);
  return ProductIn.safeParse({
    name: draft.name.trim(),
    description: orNull(draft.description),
    tagline: orNull(draft.tagline),
    price: orNull(draft.price),
    offer: orNull(draft.offer),
    colors: draft.useProductColors ? colorsIn(draft.colors) : [],
    key_benefits: rows(draft.keyBenefits),
    use_cases: rows(draft.useCases),
    images: filled.map((slot) => ({
      asset_id: slot.assetId,
      tag: slot.tag,
      is_main: slot.index === main,
    })),
  });
}
